import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import type { RootState } from '../../../store/store';
import { ROUTES } from '../../../utils/routes';
import { ROUTE_TITLES } from '../../../utils/titles';
import Badge from '../../common/Badge';
import CoproLocations from '../../common/CoproMap';
import Popup from '../../common/Popup';
import SubscriptionBanner from '../../NewDashboard/components/SubscriptionBanner';
import CommSelector from './components/CommSelector';
import CoproAverageSales from './components/CoproAverageSales';
import CoproExpired from './components/CoproExpired';
import CoproRents from './components/CoproRents';
import CoproSold from './components/CoproSold';
import KPIs from './components/KPIs';
import PerformanceAnalysis from './components/PerformanceAnalysis/PerformanceAnalysis.module';

function MarketVision() {
  const [selectedLocationIdProp, setSelectedLocationIdProp] = useState<number>(null);
  const [showValidationPopup, setShowValidationPopup] = useState(false);
  const company = useSelector((state: RootState) => state.auth.company);
  const jwt = useSelector((state: RootState) => state.auth.token);
  const navigate = useNavigate();

  useEffect(() => {
    // Check if copro validation is not done - add null check for setupSteps
    if (company?.id && company?.setupSteps && !company.setupSteps.coproValidation) {
      setShowValidationPopup(true);
    }
  }, [company?.id, company?.setupSteps?.coproValidation]);

  return (
    <div>
      {/* Mandatory validation popup */}
      <Popup
        isOpen={showValidationPopup}
        title="Validation des copropriétés requise"
        disableCloseButton={true}
      >
        <div className="text-center">
          <p className="mb-4">
            Vous devez d'abord valider vos copropriétés avant d'accéder à cette page.
          </p>
          <button
            onClick={() => navigate(ROUTES.ANALYZING_VERIFY_COPROPRIETES)}
            className="rounded bg-rgpd-blue-dark px-6 py-3 text-white shadow-md"
          >
            Valider mes copropriétés
          </button>
        </div>
      </Popup>

      <KPIs />
      {/* Warning badges for incomplete steps */}
      {(company?.id && company?.setupSteps && (!company.setupSteps.addressValidation || !company.setupSteps.addMissingCopros)) && !showValidationPopup && (
        <div className="mb-6 flex flex-col gap-4">
          <Badge
            mode="warning"
            type="outline"
            size="medium"
            className="w-full rounded-t-none"
            text={
              <div className="w-full">
                <p className="font-medium">En l'état cette page est un aperçu incomplet de vos données. Pour améliorer la qualité des données, veuillez compléter :</p>
                <div className="mt-2 flex items-center justify-center gap-10">
                  {company?.setupSteps && !company.setupSteps.addMissingCopros && (
                    <button
                      onClick={() => navigate(ROUTES.ANALYZING_ADD_COPROPRIETES)}
                      className="text-orange-800 underline hover:text-orange-900"
                    >
                      {ROUTE_TITLES[ROUTES.ANALYZING_ADD_COPROPRIETES]}
                    </button>
                  )}
                  {company?.setupSteps && !company.setupSteps.addressValidation && (
                    <button
                      onClick={() => navigate(ROUTES.ANALYZING_ADD_ADDRESSES)}
                      className="text-orange-800 underline hover:text-orange-900"
                    >
                      {ROUTE_TITLES[ROUTES.ANALYZING_ADD_ADDRESSES]}
                    </button>
                  )}
                </div>
              </div>
            }
            textAlign="left"
          />
        </div>
      )}
      <div className="my-10 grid grid-cols-2 gap-10 lg:grid-cols-3">
        <div className="col-span-2 flex flex-col justify-between">
          <CommSelector />
          <div className="my-3" />
          <CoproSold />
          <div className="my-3" />
          <CoproRents />
          <div className="my-3" />
          <PerformanceAnalysis />
          <div className="my-3" />
          <CoproExpired />
        </div>
        <div className="col-span-2 flex min-h-80 flex-col lg:col-span-1">
          <CoproLocations selectedLocationIdProp={selectedLocationIdProp} />
        </div>
      </div>
      <CoproAverageSales setSelectedLocationIdProp={setSelectedLocationIdProp} />

      <div className="mt-8">
        <SubscriptionBanner company={company} jwt={jwt} />
      </div>
    </div>
  );
}

export default MarketVision;
